import { ResponsiveBar } from "@nivo/bar";
import {
  BarChart3,
  PieChart,
  Users,
  Building2,
  Calendar,
  CheckCircle2,
  Award,
  DollarSign,
  Filter,
  Clock,
} from "lucide-react";
import { useTranslate, useCanAccess } from "ra-core";
import { useEffect, useMemo, useState } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import type { LeadSourceStat } from "@/types/realEstate";
import { useRealEstateContext } from "../providers/RealEstateContext";
import { getSupabaseClient } from "../providers/supabase/supabase";
import { formatPriceInINR } from "@/lib/formatters";

type ExtendedLeadSourceStat = LeadSourceStat & {
  contacted?: number;
  booked?: number;
  funnel?: {
    lead: number;
    contacted: number;
    visit: number;
    token: number;
    booked: number;
  };
};

type AgentScorecardRow = {
  agentId: string;
  agentName: string;
  avgFirstTouchMinutes: number | null;
  showRate: number | null;
  completedVisits: number;
  noShowVisits: number;
  bookingsCount: number;
  grossBookingValue: number;
};

type InventoryAgingRow = {
  propertyId: string;
  unitNumber: string;
  projectName: string;
  status: "Available" | "On_Hold";
  daysInStatus: number;
};

const INVENTORY_AGING_LIMIT = 25;

const formatShowRate = (
  completed: number,
  noShow: number,
  rate: number | null | undefined,
): string => {
  const attempts = completed + noShow;
  if (attempts <= 0) return "—";
  if (rate == null || !Number.isFinite(rate)) return "—";
  const pct = rate <= 1 ? rate * 100 : rate;
  return `${pct.toFixed(1)}%`;
};

const formatDateRangeLabel = (from: string, to: string): string => {
  const fromDate = new Date(`${from}T00:00:00`);
  const toDate = new Date(`${to}T23:59:59`);
  if (Number.isNaN(fromDate.getTime()) || Number.isNaN(toDate.getTime())) {
    return "";
  }
  return `${fromDate.toLocaleDateString("en-IN")} – ${toDate.toLocaleDateString("en-IN")}`;
};

const formatFirstTouch = (minutes: number | null | undefined): string => {
  if (minutes == null || !Number.isFinite(minutes)) return "—";
  return String(Math.round(minutes));
};

const getFunnelSteps = (stat: ExtendedLeadSourceStat) => {
  const funnel = stat.funnel;
  return [
    {
      key: "lead",
      value: funnel?.lead ?? stat.leads ?? 0,
    },
    {
      key: "contacted",
      value: funnel?.contacted ?? stat.contacted ?? 0,
    },
    {
      key: "visit",
      value: funnel?.visit ?? stat.siteVisits ?? 0,
    },
    {
      key: "token",
      value: funnel?.token ?? stat.tokens ?? 0,
    },
    {
      key: "booked",
      value: funnel?.booked ?? stat.booked ?? 0,
    },
  ];
};

export const RealEstateAnalytics = () => {
  const translate = useTranslate();
  const { canAccess: isAdmin } = useCanAccess({
    resource: "configuration",
    action: "edit",
  });
  const { projects, properties } = useRealEstateContext();

  const defaultFrom = useMemo(
    () => new Date(Date.now() - 30 * 86400000).toISOString(),
    [],
  );
  const [dateFrom, setDateFrom] = useState(defaultFrom.slice(0, 10));
  const [dateTo, setDateTo] = useState(new Date().toISOString().slice(0, 10));
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [asOf, setAsOf] = useState<string | null>(null);
  const [summary, setSummary] = useState({
    totalLeads: 0,
    siteVisitsConducted: 0,
    bookingsCount: 0,
    grossBookingValue: 0,
  });
  const [sourceStats, setSourceStats] = useState<ExtendedLeadSourceStat[]>([]);
  const [scorecardRows, setScorecardRows] = useState<AgentScorecardRow[]>([]);
  const [agingRows, setAgingRows] = useState<InventoryAgingRow[]>([]);
  const [cplRows, setCplRows] = useState<
    Array<{ source: string; leads: number; spend: number; cpl: number | null }>
  >([]);

  useEffect(() => {
    if (!isAdmin) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setLoadError(null);
    const fromIso = new Date(`${dateFrom}T00:00:00`).toISOString();
    const toIso = new Date(`${dateTo}T23:59:59`).toISOString();
    const supabase = getSupabaseClient();

    Promise.all([
      supabase.rpc("analytics_summary", { p_from: fromIso, p_to: toIso }),
      supabase.rpc("analytics_funnel", { p_from: fromIso, p_to: toIso }),
      supabase.rpc("analytics_agent_scorecard", {
        p_from: fromIso,
        p_to: toIso,
      }),
      supabase.rpc("analytics_inventory_aging", {
        p_limit: INVENTORY_AGING_LIMIT,
      }),
      supabase.rpc("analytics_cpl"),
    ])
      .then(([summaryRes, funnelRes, scoreRes, agingRes, cplRes]) => {
        if (cancelled) return;
        if (
          summaryRes.error ||
          funnelRes.error ||
          scoreRes.error ||
          agingRes.error ||
          cplRes.error
        ) {
          throw new Error(
            summaryRes.error?.message ||
              funnelRes.error?.message ||
              scoreRes.error?.message ||
              agingRes.error?.message ||
              cplRes.error?.message ||
              "Failed to load analytics",
          );
        }
        const sum = summaryRes.data as {
          total_leads?: number;
          site_visits_conducted?: number;
          bookings_count?: number;
          gross_booking_value?: number;
          as_of?: string;
        };
        setSummary({
          totalLeads: Number(sum.total_leads) || 0,
          siteVisitsConducted: Number(sum.site_visits_conducted) || 0,
          bookingsCount: Number(sum.bookings_count) || 0,
          grossBookingValue: Number(sum.gross_booking_value) || 0,
        });
        setAsOf(sum.as_of ? String(sum.as_of) : new Date().toISOString());

        const funnel = (funnelRes.data || []) as Array<{
          source: string;
          leads: number;
          contacted: number;
          visit: number;
          token: number;
          booked: number;
        }>;
        setSourceStats(
          funnel.map((row) => ({
            source: row.source,
            leads: row.leads,
            contacted: row.contacted,
            booked: row.booked,
            siteVisits: row.visit,
            tokens: row.token,
            cpl: "—",
            conversion:
              row.leads > 0
                ? `${((row.booked / row.leads) * 100).toFixed(1)}%`
                : "0%",
            funnel: {
              lead: row.leads,
              contacted: row.contacted,
              visit: row.visit,
              token: row.token,
              booked: row.booked,
            },
          })),
        );

        setScorecardRows(
          ((scoreRes.data || []) as Array<Record<string, unknown>>)
            .map((row) => {
              const completedVisits = Number(row.completed_visits) || 0;
              const noShowVisits = Number(row.no_show_visits) || 0;
              const visitAttempts = completedVisits + noShowVisits;
              return {
                agentId: String(row.agent_id),
                agentName: String(row.agent_name || "Agent"),
                avgFirstTouchMinutes:
                  row.avg_first_touch_minutes != null
                    ? Number(row.avg_first_touch_minutes)
                    : null,
                showRate:
                  visitAttempts > 0 ? completedVisits / visitAttempts : null,
                completedVisits,
                noShowVisits,
                bookingsCount: Number(row.bookings_count) || 0,
                grossBookingValue: Number(row.gross_booking_value) || 0,
              };
            })
            .filter(
              (row) =>
                row.completedVisits +
                  row.noShowVisits +
                  row.bookingsCount +
                  (row.avgFirstTouchMinutes != null ? 1 : 0) >
                0,
            ),
        );

        setAgingRows(
          ((agingRes.data || []) as Array<Record<string, unknown>>).map(
            (row) => ({
              propertyId: String(row.property_id),
              unitNumber: String(row.unit_number),
              projectName: String(row.project_name),
              status: row.status as "Available" | "On_Hold",
              daysInStatus: Number(row.days_in_status) || 0,
            }),
          ),
        );

        setCplRows(
          ((cplRes.data || []) as Array<Record<string, unknown>>).map(
            (row) => ({
              source: String(row.source),
              leads: Number(row.leads) || 0,
              spend: Number(row.spend) || 0,
              cpl: row.cpl != null ? Number(row.cpl) : null,
            }),
          ),
        );
      })
      .catch((e) => {
        if (!cancelled) {
          setLoadError(
            e instanceof Error ? e.message : "Failed to load analytics",
          );
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [isAdmin, dateFrom, dateTo]);

  const agingDisplay = agingRows;
  const dateRangeLabel = formatDateRangeLabel(dateFrom, dateTo);

  const analyticsMetrics = {
    totalLeads: summary.totalLeads,
    siteVisitsConducted: summary.siteVisitsConducted,
    tokensBookingsLabel: `${summary.bookingsCount} Units`,
    grossBookingValueLabel: formatPriceInINR(summary.grossBookingValue),
    usingSeed: false,
  };

  if (!isAdmin) {
    return (
      <div className="p-6 max-w-[1600px] mx-auto">
        <p className="text-muted-foreground text-sm">
          {translate("crm.analytics.admin_only", {
            _: "Analytics is available to managers only.",
          })}
        </p>
      </div>
    );
  }

  const inventoryChartData = projects.map((p) => {
    const projProps = properties.filter((pr) => pr.project_id === p.id);
    const available = projProps.filter(
      (pr) => pr.status === "Available",
    ).length;
    const tokenReceived = projProps.filter(
      (pr) => pr.status === "Token_Received" || pr.status === "On_Hold",
    ).length;
    const soldBooked = projProps.filter(
      (pr) => pr.status === "Booked" || pr.status === "Sold",
    ).length;

    return {
      project: p.name.length > 15 ? `${p.name.slice(0, 15)}...` : p.name,
      Available: available,
      "Token Received": tokenReceived,
      "Booked / Sold": soldBooked,
    };
  });

  const hasInventoryChartData = inventoryChartData.some(
    (row) => row.Available + row["Token Received"] + row["Booked / Sold"] > 0,
  );

  const funnelStepLabels: Record<string, string> = {
    lead: translate("crm.analytics.funnel.steps.lead"),
    contacted: translate("crm.analytics.funnel.steps.contacted"),
    visit: translate("crm.analytics.funnel.steps.visit"),
    token: translate("crm.analytics.funnel.steps.token"),
    booked: translate("crm.analytics.funnel.steps.booked"),
  };

  return (
    <div className="p-6 space-y-6 max-w-[1600px] mx-auto select-none">
      <div className="border-b pb-4">
        <h1 className="text-2xl font-bold tracking-tight text-foreground flex items-center gap-2">
          <BarChart3 className="w-7 h-7 text-primary" />
          {translate("crm.analytics.title")}
        </h1>
        <p className="text-muted-foreground text-sm mt-1">
          {translate("crm.analytics.subtitle")}
          {dateRangeLabel && (
            <span className="ml-2 text-xs">
              {translate("crm.analytics.date_range", {
                _: "Range",
              })}{" "}
              {dateRangeLabel}
            </span>
          )}
          {asOf && (
            <span className="ml-2 text-xs">
              {translate("crm.analytics.as_of", {
                _: "As of",
              })}{" "}
              {new Date(asOf).toLocaleString()}
            </span>
          )}
          {loadError && (
            <span className="ml-1 text-red-600 dark:text-red-400">
              {loadError}
            </span>
          )}
        </p>
        <div className="flex flex-wrap items-center gap-2 mt-3">
          <label className="text-xs text-muted-foreground flex items-center gap-1">
            From
            <input
              type="date"
              className="border rounded px-2 py-1 text-sm bg-background"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
            />
          </label>
          <label className="text-xs text-muted-foreground flex items-center gap-1">
            To
            <input
              type="date"
              className="border rounded px-2 py-1 text-sm bg-background"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
            />
          </label>
          {loading && (
            <span className="text-xs text-muted-foreground">
              {translate("ra.page.loading", { _: "Loading..." })}
            </span>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="border-muted">
          <CardHeader className="flex flex-row items-center justify-between pb-2 space-y-0">
            <CardTitle className="text-xs font-semibold text-muted-foreground uppercase">
              {translate("crm.analytics.metrics.total_leads")}
            </CardTitle>
            <Users className="w-4 h-4 text-primary" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {analyticsMetrics.totalLeads}
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              {translate("crm.analytics.metrics.total_leads_source", {
                _: "Leads with first activity in selected range",
              })}
            </p>
          </CardContent>
        </Card>

        <Card className="border-muted">
          <CardHeader className="flex flex-row items-center justify-between pb-2 space-y-0">
            <CardTitle className="text-xs font-semibold text-muted-foreground uppercase">
              {translate("crm.analytics.metrics.site_visits")}
            </CardTitle>
            <Calendar className="w-4 h-4 text-primary" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {analyticsMetrics.siteVisitsConducted}
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              {translate("crm.analytics.metrics.site_visits_count", {
                count: summary.siteVisitsConducted,
              })}
            </p>
          </CardContent>
        </Card>

        <Card className="border-muted">
          <CardHeader className="flex flex-row items-center justify-between pb-2 space-y-0">
            <CardTitle className="text-xs font-semibold text-muted-foreground uppercase">
              {translate("crm.analytics.metrics.tokens_bookings")}
            </CardTitle>
            <CheckCircle2 className="w-4 h-4 text-emerald-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-emerald-600 dark:text-emerald-400">
              {analyticsMetrics.tokensBookingsLabel}
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              {translate("crm.analytics.metrics.tokens_bookings_source")}
            </p>
          </CardContent>
        </Card>

        <Card className="border-muted">
          <CardHeader className="flex flex-row items-center justify-between pb-2 space-y-0">
            <CardTitle className="text-xs font-semibold text-muted-foreground uppercase">
              {translate("crm.analytics.metrics.gross_booking_value")}
            </CardTitle>
            <DollarSign className="w-4 h-4 text-primary" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-primary">
              {analyticsMetrics.grossBookingValueLabel}
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              {translate("crm.analytics.metrics.gross_booking_value_source")}
            </p>
          </CardContent>
        </Card>
      </div>

      <Card className="border-muted">
        <CardHeader>
          <CardTitle className="text-lg font-bold flex items-center gap-2">
            <Filter className="w-5 h-5 text-primary" />
            {translate("crm.analytics.funnel.title")}
          </CardTitle>
          <CardDescription>
            {translate("crm.analytics.funnel.description")}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {sourceStats.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              {translate("crm.analytics.funnel.empty")}
            </p>
          ) : (
            sourceStats.map((stat) => {
              const steps = getFunnelSteps(stat);
              const max = Math.max(...steps.map((s) => s.value), 1);
              return (
                <div key={stat.source} className="space-y-2">
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-sm font-semibold text-foreground">
                      {stat.source}
                    </span>
                    <span className="text-xs text-muted-foreground">
                      {translate("crm.analytics.funnel.conversion", {
                        rate: stat.conversion,
                      })}
                    </span>
                  </div>
                  <div className="grid grid-cols-5 gap-2">
                    {steps.map((step) => (
                      <div key={step.key} className="space-y-1 min-w-0">
                        <div className="text-[10px] uppercase tracking-wide text-muted-foreground truncate">
                          {funnelStepLabels[step.key]}
                        </div>
                        <div className="h-16 rounded-md bg-muted/40 flex items-end overflow-hidden">
                          <div
                            className="w-full bg-primary/80 rounded-t-sm transition-all min-h-[4px]"
                            style={{
                              height: `${Math.max(4, (step.value / max) * 100)}%`,
                            }}
                            title={`${funnelStepLabels[step.key]}: ${step.value}`}
                          />
                        </div>
                        <div className="text-xs font-semibold text-center">
                          {step.value}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })
          )}
        </CardContent>
      </Card>

      <Card className="border-muted">
        <CardHeader>
          <CardTitle className="text-lg font-bold flex items-center gap-2">
            <BarChart3 className="w-5 h-5 text-primary" />
            {translate("crm.analytics.portal_volume.title")}
          </CardTitle>
          <CardDescription>
            {translate("crm.analytics.portal_volume.description")}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="h-72 w-full">
            <ResponsiveBar
              data={
                sourceStats.length > 0
                  ? sourceStats.map((stat) => ({
                      source: stat.source,
                      leads: Number(stat.leads) || 0,
                      siteVisits: Number(stat.siteVisits) || 0,
                      tokens: Number(stat.tokens) || 0,
                    }))
                  : [
                      {
                        source: translate("crm.analytics.no_data"),
                        leads: 0,
                        siteVisits: 0,
                        tokens: 0,
                      },
                    ]
              }
              keys={["leads", "siteVisits", "tokens"]}
              indexBy="source"
              margin={{ top: 20, right: 30, bottom: 40, left: 50 }}
              padding={0.3}
              groupMode="grouped"
              colors={["#3b82f6", "#10b981", "#f59e0b"]}
              borderWidth={1}
              borderColor={{ from: "color", modifiers: [["darker", 0.2]] }}
              axisTop={null}
              axisRight={null}
              axisBottom={{
                tickSize: 5,
                tickPadding: 5,
                tickRotation: 0,
              }}
              axisLeft={{
                tickSize: 5,
                tickPadding: 5,
                tickRotation: 0,
              }}
              labelSkipWidth={12}
              labelSkipHeight={12}
              labelTextColor="#ffffff"
              legends={[
                {
                  dataFrom: "keys",
                  anchor: "top-right",
                  direction: "row",
                  justify: false,
                  translateX: 0,
                  translateY: -20,
                  itemsSpacing: 20,
                  itemWidth: 100,
                  itemHeight: 20,
                  itemDirection: "left-to-right",
                  itemOpacity: 0.85,
                  symbolSize: 12,
                },
              ]}
              theme={{
                axis: {
                  ticks: {
                    text: { fill: "#888888", fontSize: 11 },
                  },
                },
                grid: {
                  line: { stroke: "#e2e8f0", strokeWidth: 1 },
                },
              }}
            />
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="border-muted">
          <CardHeader>
            <CardTitle className="text-lg font-bold flex items-center gap-2">
              <PieChart className="w-5 h-5 text-primary" />
              {translate("crm.analytics.portal_roi.title")}
            </CardTitle>
            <CardDescription>
              {translate("crm.analytics.portal_roi.description")}
            </CardDescription>
          </CardHeader>
          <CardContent className="p-0 overflow-x-auto">
            <table className="w-full text-xs text-left">
              <thead className="bg-muted/50 text-muted-foreground uppercase border-b">
                <tr>
                  <th className="p-3">
                    {translate("crm.analytics.portal_roi.columns.source")}
                  </th>
                  <th className="p-3">
                    {translate("crm.analytics.portal_roi.columns.leads")}
                  </th>
                  <th className="p-3">
                    {translate("crm.analytics.portal_roi.columns.contacted")}
                  </th>
                  <th className="p-3">
                    {translate("crm.analytics.portal_roi.columns.visits")}
                  </th>
                  <th className="p-3">
                    {translate("crm.analytics.portal_roi.columns.tokens")}
                  </th>
                  <th className="p-3">
                    {translate("crm.analytics.portal_roi.columns.booked")}
                  </th>
                  <th className="p-3">
                    {translate("crm.analytics.portal_roi.columns.cpl")}
                  </th>
                  <th className="p-3 text-right">
                    {translate("crm.analytics.portal_roi.columns.conversion")}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {sourceStats.length === 0 ? (
                  <tr>
                    <td
                      colSpan={8}
                      className="p-4 text-center text-muted-foreground"
                    >
                      {translate("crm.analytics.portal_roi.empty", {
                        _: "No leads in this date range.",
                      })}
                    </td>
                  </tr>
                ) : (
                  sourceStats.map((item, idx) => (
                    <tr key={idx} className="hover:bg-muted/30">
                      <td className="p-3 font-bold text-foreground">
                        {item.source}
                      </td>
                      <td className="p-3 font-semibold">{item.leads}</td>
                      <td className="p-3">{item.contacted ?? "—"}</td>
                      <td className="p-3">{item.siteVisits}</td>
                      <td className="p-3 font-bold text-emerald-600">
                        {item.tokens}
                      </td>
                      <td className="p-3">{item.booked ?? "—"}</td>
                      <td className="p-3 text-muted-foreground">
                        {(() => {
                          const cpl = cplRows.find(
                            (c) => c.source === item.source,
                          )?.cpl;
                          return cpl != null
                            ? `₹${Math.round(cpl).toLocaleString("en-IN")}`
                            : item.cpl || "—";
                        })()}
                      </td>
                      <td className="p-3 text-right font-bold text-primary">
                        {item.conversion}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </CardContent>
        </Card>

        <Card className="border-muted">
          <CardHeader>
            <CardTitle className="text-lg font-bold flex items-center gap-2">
              <Award className="w-5 h-5 text-primary" />
              {translate("crm.analytics.agent_scorecard.title")}
            </CardTitle>
            <CardDescription>
              {translate("crm.analytics.agent_scorecard.description")}
            </CardDescription>
          </CardHeader>
          <CardContent className="p-0 overflow-x-auto">
            <table className="w-full text-xs text-left">
              <thead className="bg-muted/50 text-muted-foreground uppercase border-b">
                <tr>
                  <th className="p-3">
                    {translate("crm.analytics.agent_scorecard.columns.agent")}
                  </th>
                  <th className="p-3">
                    {translate(
                      "crm.analytics.agent_scorecard.columns.first_touch",
                    )}
                  </th>
                  <th className="p-3">
                    {translate(
                      "crm.analytics.agent_scorecard.columns.show_rate",
                    )}
                  </th>
                  <th className="p-3">
                    {translate(
                      "crm.analytics.agent_scorecard.columns.completed_visits",
                    )}
                  </th>
                  <th className="p-3">
                    {translate(
                      "crm.analytics.agent_scorecard.columns.bookings",
                    )}
                  </th>
                  <th className="p-3 text-right">
                    {translate(
                      "crm.analytics.agent_scorecard.columns.gross_value",
                    )}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {scorecardRows.length === 0 ? (
                  <tr>
                    <td
                      colSpan={6}
                      className="p-4 text-center text-muted-foreground"
                    >
                      {translate("crm.analytics.agent_scorecard.empty")}
                    </td>
                  </tr>
                ) : (
                  scorecardRows.map((agent, idx) => (
                    <tr
                      key={agent.agentId || idx}
                      className="hover:bg-muted/30"
                    >
                      <td className="p-3 font-bold text-foreground flex items-center gap-2">
                        <Badge
                          variant="outline"
                          className="w-5 h-5 rounded-full p-0 flex items-center justify-center text-[10px]"
                        >
                          #{idx + 1}
                        </Badge>
                        {agent.agentName}
                      </td>
                      <td className="p-3">
                        {formatFirstTouch(agent.avgFirstTouchMinutes)}
                      </td>
                      <td className="p-3 font-medium">
                        {formatShowRate(
                          agent.completedVisits,
                          agent.noShowVisits,
                          agent.showRate,
                        )}
                      </td>
                      <td className="p-3">{agent.completedVisits}</td>
                      <td className="p-3 font-bold text-emerald-600">
                        {agent.bookingsCount}
                      </td>
                      <td className="p-3 text-right font-bold text-primary">
                        {formatPriceInINR(agent.grossBookingValue)}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        <Card className="border-muted">
          <CardHeader>
            <CardTitle className="text-lg font-bold flex items-center gap-2">
              <Clock className="w-5 h-5 text-primary" />
              {translate("crm.analytics.inventory_aging.title")}
            </CardTitle>
            <CardDescription>
              {translate("crm.analytics.inventory_aging.description")}
            </CardDescription>
          </CardHeader>
          <CardContent className="p-0 overflow-x-auto">
            {agingRows.length > INVENTORY_AGING_LIMIT && (
              <p className="px-4 py-2 text-xs text-muted-foreground border-b">
                {translate("crm.analytics.inventory_aging.showing", {
                  shown: agingDisplay.length,
                  total: agingRows.length,
                })}
              </p>
            )}
            <p className="px-4 py-2 text-xs text-muted-foreground border-b">
              {translate("crm.analytics.inventory_aging.available_footnote")}
            </p>
            <table className="w-full text-xs text-left">
              <thead className="bg-muted/50 text-muted-foreground uppercase border-b">
                <tr>
                  <th className="p-3">
                    {translate("crm.analytics.inventory_aging.columns.unit")}
                  </th>
                  <th className="p-3">
                    {translate("crm.analytics.inventory_aging.columns.project")}
                  </th>
                  <th className="p-3">
                    {translate("crm.analytics.inventory_aging.columns.status")}
                  </th>
                  <th className="p-3 text-right">
                    {translate("crm.analytics.inventory_aging.columns.days")}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {agingDisplay.length === 0 ? (
                  <tr>
                    <td
                      colSpan={4}
                      className="p-4 text-center text-muted-foreground"
                    >
                      {translate("crm.analytics.inventory_aging.empty")}
                    </td>
                  </tr>
                ) : (
                  agingDisplay.map((row) => (
                    <tr key={row.propertyId} className="hover:bg-muted/30">
                      <td className="p-3 font-bold text-foreground">
                        {row.unitNumber}
                      </td>
                      <td className="p-3">{row.projectName}</td>
                      <td className="p-3">
                        <Badge
                          variant={
                            row.status === "Available" ? "default" : "outline"
                          }
                        >
                          {row.status === "On_Hold"
                            ? translate(
                                "crm.analytics.inventory_aging.status.on_hold",
                              )
                            : translate(
                                "crm.analytics.inventory_aging.status.available",
                              )}
                        </Badge>
                      </td>
                      <td className="p-3 text-right font-semibold">
                        {row.daysInStatus}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </CardContent>
        </Card>

        <Card className="border-muted">
          <CardHeader>
            <CardTitle className="text-lg font-bold flex items-center gap-2">
              <Building2 className="w-5 h-5 text-primary" />
              {translate("crm.analytics.inventory_health.title")}
            </CardTitle>
            <CardDescription>
              {translate("crm.analytics.inventory_health.description")}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {!hasInventoryChartData ? (
              <p className="text-sm text-muted-foreground py-8 text-center">
                {translate("crm.analytics.inventory_health.empty", {
                  _: "No inventory loaded yet — open Properties or refresh after seeding units.",
                })}
              </p>
            ) : (
              <div className="h-80 w-full">
                <ResponsiveBar
                  data={inventoryChartData}
                  keys={["Available", "Token Received", "Booked / Sold"]}
                  indexBy="project"
                  margin={{ top: 20, right: 30, bottom: 50, left: 60 }}
                  padding={0.35}
                  colors={["#10b981", "#f59e0b", "#4f46e5"]}
                  borderWidth={1}
                  borderColor={{ from: "color", modifiers: [["darker", 0.2]] }}
                  axisTop={null}
                  axisRight={null}
                  axisBottom={{
                    tickSize: 5,
                    tickPadding: 5,
                    tickRotation: 0,
                  }}
                  axisLeft={{
                    tickSize: 5,
                    tickPadding: 5,
                    tickRotation: 0,
                  }}
                  labelSkipWidth={12}
                  labelSkipHeight={12}
                  labelTextColor="#ffffff"
                  legends={[
                    {
                      dataFrom: "keys",
                      anchor: "top-right",
                      direction: "row",
                      justify: false,
                      translateX: 0,
                      translateY: -20,
                      itemsSpacing: 20,
                      itemWidth: 120,
                      itemHeight: 20,
                      itemDirection: "left-to-right",
                      itemOpacity: 0.85,
                      symbolSize: 12,
                    },
                  ]}
                  theme={{
                    axis: {
                      ticks: {
                        text: { fill: "#888888", fontSize: 11 },
                      },
                    },
                    grid: {
                      line: { stroke: "#e2e8f0", strokeWidth: 1 },
                    },
                  }}
                />
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default RealEstateAnalytics;
